import { AxiosError } from 'axios'

import { ErrorCode } from '@app/routing/RouterToaster'

import { ChildError, PropertyDetailResponseType, ResponseError } from '@utils/ApiClient'
import { isServiceUnavailableStatus } from '@utils/httpStatus'
import { JSONParse } from '@utils/json'

export interface MaintenanceData {
  Message: string
  StartDateTime: string
  EndDateTime: string
}

export interface ApiErrorBody {
  code?: string
  Message?: string
  message?: string | any
  localizedMessage?: string
  properties?: Record<string, PropertyDetailResponseType>
  ChildErrors?: ChildError[]
  [key: string]: any
}

const initialMaintenanceMessage = {
  Message: 'Scheduled Maintenance',
  StartDateTime: '',
  EndDateTime: '',
}

interface ErrorResponseDataProps {
  response?: AxiosError<ApiErrorBody>
  status?: number
  config?: Record<string, unknown> | undefined
  errorCode: ErrorCode
  errorMessage: string
  configApiUrl?: string
  isMaintenanceMode: boolean
  maintenance: MaintenanceData | undefined
}

export const getErrorResponseData = (error: ResponseError): ErrorResponseDataProps => {
  const response = error?.response as AxiosError<ApiErrorBody> | undefined

  const status = response?.response?.status
  const configApiUrl = error?.response?.config?.url
  const headers = extractHeaders(response)
  const errorCode = extractErrorCode(response)
  const errorMessage = extractErrorMessage(response)

  const config = JSONParse<Record<string, unknown>>(error?.response?.config?.data) || undefined

  const isMaintenanceMode = isMaintenance(response)
  const maintenance = isMaintenanceMode
    ? getHeaderJSONByKey<MaintenanceData>(headers, 'x-maintenance') ?? initialMaintenanceMessage
    : undefined

  return {
    response,
    status,
    config,
    errorCode,
    errorMessage,
    configApiUrl,
    isMaintenanceMode,
    maintenance: isMaintenanceMode
      ? maintenance ?? { Message: 'Scheduled Maintenance', StartDateTime: '', EndDateTime: '' }
      : undefined,
  }
}

const extractHeaders = (response?: AxiosError<ApiErrorBody>): Record<string, string> => {
  return (response?.response?.headers as unknown as Record<string, string>) || {}
}

const extractErrorCode = (response?: AxiosError<ApiErrorBody>): ErrorCode => {
  if (!response?.response?.data) return '' as ErrorCode

  const data = response.response.data
  const rawCode = data.code || ''

  const message = JSONParse<{ code: string }>(response?.response?.data?.message)

  return (message?.code || rawCode) as ErrorCode
}

const extractErrorMessage = (response?: AxiosError<ApiErrorBody>): string => {
  if (!response?.response?.data) return ''

  const data = response.response.data

  return data.localizedMessage || data.message || ''
}

const getHeaderJSONByKey = <T>(headers: Record<string, string>, key: string): T | undefined => {
  const value = getHeaderByKey(headers, key)
  return JSONParse<T>(value || '{}')
}

const getHeaderByKey = (headers: Record<string, string>, headerKey: string): string | undefined => {
  const newHeaderKey = Object.keys(headers).find(
    (key) => key.toLowerCase() === headerKey.toLowerCase()
  )
  return newHeaderKey ? headers[newHeaderKey] : ''
}

export const getPspServiceCheckErrorMessage = (error: ResponseError): string | undefined => {
  const { response } = getErrorResponseData(error)
  const rawMessage = response?.response?.data?.message as string | undefined
  const messages = rawMessage?.split(';') || []
  const [, message] = messages.filter((x: string) => !!x).map((x: string) => x.trim())
  return message
}

export const isIpApiCall = (error: ResponseError): boolean => {
  const { configApiUrl } = getErrorResponseData(error)
  return !!configApiUrl?.includes('ipapi')
}

export const isMaintenance = (response?: AxiosError<ApiErrorBody>): boolean => {
  if (!response || !isServiceUnavailableStatus(response?.response?.status)) {
    return false
  }

  const headers = response?.response?.headers || {}
  const maintenanceHeader = Object.keys(headers).find(
    (key) => key.toLowerCase() === 'x-maintenance'
  )

  return !!maintenanceHeader
}

export const is2FAAlreadyEnabled = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const { errorCode: code } = getErrorResponseData(error as ResponseError)
  return code === 'two_factor_authentication_already_enabled'
}

export const is404Response = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const { status } = getErrorResponseData(error as ResponseError)
  return status === 404
}

export const hasChildErrors = (error: ResponseError): boolean => {
  const response = error?.response as AxiosError<ApiErrorBody> | undefined
  const errorData = response?.response?.data
  return !!(
    errorData?.ChildErrors &&
    Array.isArray(errorData.ChildErrors) &&
    errorData.ChildErrors.length > 0
  )
}

export const getChildErrors = (error: ResponseError): ChildError[] => {
  const response = error?.response as AxiosError<ApiErrorBody> | undefined
  const errorData = response?.response?.data
  return errorData?.ChildErrors || []
}
